import {
  Add,
  AutoAwesomeOutlined,
  CheckCircleOutlineOutlined,
  Close,
  DashboardOutlined,
  HourglassEmpty,
  Menu,
  SaveOutlined,
} from '@mui/icons-material'
import { Box, Drawer, IconButton, Stack } from '@mui/material'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useIsOmneansConvIntake } from '@omnea-app/fe-chat/useIsOmneansConvIntake'
import { useFeatureFlags } from '@omnea-app/fe-org-settings/FeatureFlagsProvider'
import { generateRequestsListRoute } from '@omnea-app/fe-requests/requestRoutes'
import { InternalLinkButton } from '@omnea-app/fe-routing/InternalLinkButton'
import { useGetMyTaskCount } from '@omnea-app/fe-tasks/useTasks'
import { ExpandableSidebarLink } from '../sidebar/ExpandableSidebarLink'
import { resolvePage } from '../sidebar/Sidebar'
import { SidebarLink } from '../sidebar/SidebarLink'
import { SidebarPages } from '../sidebar/SidebarPages'
import { SidebarDivider, SidebarItemWrapper, SidebarList } from '../sidebar/StyledSidebarComponents'

export const MobileMenu = () => {
  const [isOpen, setIsOpen] = useState(false)
  const { query, pathname, asPath } = useRouter()
  const { page: selectedPage } = resolvePage(pathname, query)
  const { t } = useTranslation('common')

  const { data: taskCount = 0 } = useGetMyTaskCount()

  const { AI_CHAT_ASSISTANT } = useFeatureFlags()
  const { convIntakeEnabledForCurrentUser } = useIsOmneansConvIntake()

  const handleClose = () => setIsOpen(false)

  const isSettingsPage = pathname.startsWith('/settings')

  return (
    <>
      <IconButton onClick={() => setIsOpen(!isOpen)} aria-label={t('sidebar.open-menu')}>
        <Menu />
      </IconButton>
      <Drawer
        open={isOpen}
        onClose={handleClose}
        slotProps={{ paper: { sx: { border: 'none', borderRadius: 0 } } }}
        anchor="left"
      >
        <Stack sx={{ minWidth: 'min(70vw, 300px)', height: '100%' }}>
          <Stack direction="row" justifyContent="space-between" py={2} px={4}>
            <Stack component={Link} href="/" passHref direction="row" alignItems="center">
              <Image src="/logo-dark.svg" alt="Omnea" height={24} width={92} priority />
            </Stack>

            <IconButton onClick={() => setIsOpen(!isOpen)} aria-label={t('sidebar.close-menu')}>
              <Close />
            </IconButton>
          </Stack>

          <SidebarDivider />
          <SidebarList>
            <SidebarLink
              href="/"
              title={t('sidebar.dashboard')}
              selected={selectedPage === SidebarPages.HOME && !isSettingsPage}
              Icon={DashboardOutlined}
              isSidebarExpanded={true}
            />

            {AI_CHAT_ASSISTANT || convIntakeEnabledForCurrentUser ? (
              <SidebarLink
                href="/ai"
                title={t('sidebar.omnea-ai')}
                selected={selectedPage === SidebarPages.AI}
                Icon={AutoAwesomeOutlined}
                isSidebarExpanded={true}
              />
            ) : null}

            <SidebarLink
              href="/tasks"
              title={t('sidebar.tasks')}
              selected={selectedPage === SidebarPages.TASKS && !isSettingsPage}
              Icon={CheckCircleOutlineOutlined}
              isSidebarExpanded={true}
              count={taskCount}
            />

            <SidebarLink
              href="/start/drafts"
              title={t('sidebar.drafts')}
              selected={selectedPage === SidebarPages.DRAFTS}
              Icon={SaveOutlined}
              isSidebarExpanded={true}
            />

            <SidebarLink
              href={generateRequestsListRoute()}
              title={t('sidebar.resources-requests')}
              selected={selectedPage === SidebarPages.REQUESTS && !isSettingsPage}
              Icon={HourglassEmpty}
              isSidebarExpanded={true}
            />
            <ExpandableSidebarLink
              items={[
                {
                  type: SidebarPages.HOME,
                  title: t('sidebar.notifications'),
                  href: '/settings/notifications',
                  selected: asPath === '/settings/notifications',
                },
                {
                  type: SidebarPages.HOME,
                  title: t('sidebar.preferences'),
                  href: '/settings/preferences',
                  selected: asPath === '/settings/preferences',
                },
              ]}
              isSidebarExpanded={true}
              title={t('sidebar.settings')}
              Icon={DashboardOutlined}
              selected={asPath.startsWith('/settings')}
              onItemClick={handleClose}
            />
          </SidebarList>

          <Box mt="auto">
            <SidebarList>
              <SidebarItemWrapper>
                <InternalLinkButton
                  variant="contained"
                  href="/start"
                  fullWidth
                  startIcon={<Add />}
                  color="primary"
                  onClick={handleClose}
                >
                  {t('sidebar.create-request')}
                </InternalLinkButton>
              </SidebarItemWrapper>
            </SidebarList>
          </Box>
        </Stack>
      </Drawer>
    </>
  )
}
